import { useEffect, useMemo, useRef } from 'react';
import { isEmpty, omit } from 'lodash';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useSetDialogs from '@/components/EditorBuilder/Settings/hooks/useSetDialogs';
import { EditorBuilderActions } from '@/components/EditorBuilder/types';
import {
  onSetCommentThreadId,
  onSetImportLessonAttrs,
  onSetLessonAssignee,
  onUpdateContentText,
  onUpdateLessonStatus,
} from '@/components/EditorBuilder/utils';
import { GenerativeTaskFragment } from '@/components/LyearnAI/graphql/fragments/generated/GenerativeTaskFragment';
import { Blocks } from '@/components/PageEditor/types';
import { ContentType } from '@/modules/corporate/modules/course/views/CourseBuilder/components/Sidebar/types';
import { ArticleContentType, MediaKindEnum, UpdateArticleInput } from '@/types/schema';

import { EMPTY_ARTICLE_TITLE } from '../consts';
import { DEFAULT_ARTICLE_DESCRIPTION } from '../consts';
import { ArticleActions } from '../enums';
import {
  ActionPayloadType,
  onArticleCoverPayloadType,
  onImportLessonPayloadType,
  onRenamePayloadType,
  onSetArticleCustomDurationSavePayloadType,
  onSetCommentThreadIdPayloadType,
  onSetImportDialog,
  onSetImportLessonAttrsPayloadType,
  onSetLessonAssigneePayloadType,
  onUpdateContentTextType,
  onUpdateLessonStatusPayloadType,
} from '../types';
import {
  getLessonDetails,
  onRenameSectionOrLesson,
  onSetArticleCustomDurationSave,
  onSetLessonCover,
} from '../utils/articleActions';
import useArticleNavigation from './useArticleNavigation';
import useArticlePublish from './useArticlePublish';
import useUpdateArticle from './useUpdateArticle';

type UseArticleActionsProps = {
  article?: ArticleContentType;
  articleId?: string;
  data?: ContentType;
  setData?: (data: Partial<ContentType>) => void;
  generativeTask?: GenerativeTaskFragment | null;
};

const useArticleActions = ({
  article,
  articleId,
  data,
  generativeTask,
  setData,
}: UseArticleActionsProps) => {
  const { onBack } = useArticleNavigation();
  const { t } = useTranslation();
  const { showErrorSnackbar } = useSnackbar();
  const articleUpdateData = useRef<UpdateArticleInput>({});
  const { onUpdateArticle } = useUpdateArticle(article, articleId);
  const [importDialogOpen, { on, off }] = useToggle(false);

  const articleContent = useMemo(
    () => ({
      id: data?.attrs?.id,
      duration: data?.attrs?.duration,
      ...getLessonDetails(data?.content),
    }),
    [data?.attrs?.duration, data?.attrs?.id, data?.content],
  );

  const onAction = useEvent(
    async (type: ArticleActions | EditorBuilderActions, payload?: ActionPayloadType) => {
      const articleData = articleUpdateData.current;
      try {
        switch (type) {
          case ArticleActions.RenameArticle: {
            onRenameSectionOrLesson(payload as onRenamePayloadType, data?.content);
            articleData.name = (payload as onRenamePayloadType).value || EMPTY_ARTICLE_TITLE;
            break;
          }
          case EditorBuilderActions.UpdateContentText: {
            onUpdateContentText(payload as onUpdateContentTextType, data?.content);
            if ((payload as onUpdateContentTextType).type === Blocks.DESCRIPTION) {
              articleData.description = (payload as onUpdateContentTextType).value;
            }
            break;
          }
          case ArticleActions.SaveArticleCustomDuration: {
            onSetArticleCustomDurationSave(
              payload as onSetArticleCustomDurationSavePayloadType,
              data?.content,
            );
            break;
          }
          case ArticleActions.SetArticleCover: {
            onSetLessonCover(payload as onArticleCoverPayloadType, data?.content);
            articleData.cover = {
              url: (payload as onArticleCoverPayloadType).picture,
              kind: MediaKindEnum.Image,
            };
            break;
          }
          case ArticleActions.Back: {
            onBack();
            break;
          }
          case EditorBuilderActions.SetLessonAssignee: {
            onSetLessonAssignee(payload as onSetLessonAssigneePayloadType, data?.content);
            break;
          }
          case EditorBuilderActions.UpdateLessonStatus: {
            onUpdateLessonStatus(payload as onUpdateLessonStatusPayloadType, data?.content);
            break;
          }
          case EditorBuilderActions.SetCommentThreadId: {
            onSetCommentThreadId(payload as onSetCommentThreadIdPayloadType, data?.content);
            break;
          }
          case EditorBuilderActions.SetImportDialog: {
            if ((payload as onSetImportDialog).hide) {
              off();
            } else {
              on();
            }
            break;
          }
          case EditorBuilderActions.ImportLesson:
          case EditorBuilderActions.SetImportLessonAttrs: {
            if ((payload as onImportLessonPayloadType).title) {
              onRenameSectionOrLesson(
                { value: (payload as onImportLessonPayloadType).title },
                data?.content,
              );
              articleData.name =
                (payload as onImportLessonPayloadType).title || EMPTY_ARTICLE_TITLE;
              payload = omit(payload, 'title');
            }
            onSetImportLessonAttrs(payload as onSetImportLessonAttrsPayloadType, data?.content);
            off();
            break;
          }
          default:
            throw new Error('unhandled type');
        }
      } catch (e) {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      }
      setData?.({ ...data });
      if (!isEmpty(articleData)) {
        onUpdateArticle(articleData)!.then(() => {
          articleUpdateData.current = {};
        });
      }
    },
  );

  useEffect(() => {
    const articleFromTopic = generativeTask?.result.articleFromTopic;

    if (articleFromTopic?.title && articleContent.title === EMPTY_ARTICLE_TITLE) {
      onAction(ArticleActions.RenameArticle, {
        value: articleFromTopic?.title,
      });
    }

    if (articleFromTopic?.subtitle && articleContent.description === DEFAULT_ARTICLE_DESCRIPTION) {
      onAction(EditorBuilderActions.UpdateContentText, {
        type: Blocks.DESCRIPTION,
        value: articleFromTopic?.subtitle,
      });
    }
  }, [
    articleContent.description,
    articleContent.title,
    generativeTask,
    generativeTask?.result.articleFromTopic?.subtitle,
    generativeTask?.result.articleFromTopic?.title,
    onAction,
  ]);

  const { setDialog, activeDialog } = useSetDialogs({ onAction });

  const { onPublish, fetching } = useArticlePublish(onAction, article, setDialog);

  return {
    articleContent,
    onAction,
    fetching,
    onPublish,
    importDialogOpen,
    setDialog,
    activeDialog,
  };
};

export default useArticleActions;
